/**
 * Todo Metadata Grid Component
 * Displays todo metadata: Basecamp direct data first, then frontmatter from description.
 * Assignees render inside the grid when present.
 * Due/description/frontmatter editing is in BasecampTodoEditPane (Edit button).
 */

import type { ComponentType, ReactNode } from 'react';
import {
  Calendar,
  CalendarCheck,
  CircleDot,
  Clock,
  Flag,
  GitBranch,
  GitMerge,
  Link2,
  Layers,
  MessageSquare,
  OctagonAlert,
  Rows3,
  Tags,
  User,
  UserCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { parseLocalDate } from '@/lib/utils';
import type { BasecampTodo } from '@/types/basecamp';
import { TodoPersonChip } from './todo-person-chip';
import { BASECAMP_DUE_BADGE_COMPACT_CLASS, BasecampDueBadge } from './basecamp-due-badge';

interface TodoMetadataGridProps {
  todo: BasecampTodo;
  onOpenComments?: () => void;
}

/** Extract YAML body between --- fences from HTML or plain text. */
function extractFrontmatterBody(description: string): string | null {
  // BC5 Lexxy: each line is its own <div>---</div> / <div>key: value</div>
  // (stripFrontmatterBlock in todo-description.tsx uses the same shape)
  const divFmMatch = description.match(
    /<div[^>]*>\s*-{3}\s*<\/div>([\s\S]*?)<div[^>]*>\s*-{3}\s*<\/div>/i,
  );
  if (divFmMatch) {
    const lines: string[] = [];
    const divRe = /<div[^>]*>([\s\S]*?)<\/div>/gi;
    let m: RegExpExecArray | null;
    while ((m = divRe.exec(divFmMatch[1])) !== null) {
      const text = m[1].replace(/<[^>]+>/g, '').trim();
      if (text && text !== '---') lines.push(text);
    }
    if (lines.length > 0) return lines.join('\n');
  }

  // <pre>---</pre> or legacy <pre><code>---</code></pre>, else raw --- block
  const preMatch = description.match(/<pre[^>]*>([\s\S]*?)<\/pre>/i);
  let content = preMatch ? preMatch[1] : description;
  const codeMatch = content.match(/<code[^>]*>([\s\S]*?)<\/code>/i);
  if (codeMatch) content = codeMatch[1];
  const fmMatch = content.match(/---\s*\n([\s\S]*?)\n---/);
  return fmMatch ? fmMatch[1] : null;
}

/** Parse frontmatter from todo description HTML (extracts ---\nkey: value\n--- block). Exported for BasecampTodoEditPane. */
export function parseFrontmatterFromDescription(description: string): Record<string, string> {
  if (!description || typeof description !== 'string') return {};
  const fmBody = extractFrontmatterBody(description);
  if (!fmBody) return {};
  const result: Record<string, string> = {};
  for (const line of fmBody.split('\n')) {
    const m = line.match(/^([\w_-]+):\s*(.+)$/);
    if (m) {
      let v = m[2].trim();
      if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
        v = v.slice(1, -1);
      }
      result[m[1].trim()] = v;
    }
  }
  return result;
}

/** Human-readable labels for frontmatter keys. Exported for BasecampTodoEditPane. */
export const FRONTMATTER_LABELS: Record<string, string> = {
  status: 'Status',
  priority: 'Priority',
  category: 'Category',
  complexity: 'Complexity',
  tags: 'Tags',
  completed_date: 'Completed',
  mr_url: 'MR',
  reviewed_by: 'Reviewed by',
  blocker: 'Blockers',
  blockers: 'Blockers',
  estimated_time: 'Est. time',
  git_mr_url: 'MR',
  working_branch: 'Branch',
  branch: 'Branch',
  related_todos: 'Related todos',
};

/**
 * Fields to show in order (skip if not present).
 * Includes YAML status/tags. A completed todo shows a check beside the toolbar title, not an Overdue pill.
 * Exported for BasecampTodoEditPane.
 */
export const FRONTMATTER_ORDER = [
  'status',
  'priority',
  'category',
  'complexity',
  'tags',
  'estimated_time',
  'blockers',
  'blocker',
  'related_todos',
  'completed_date',
  'mr_url',
  'git_mr_url',
  'reviewed_by',
  'working_branch',
  'branch',
];

/** Keys present in notes YAML only - never invent empty defaults. Deduped. */
export function existingFrontmatterKeys(fm: Record<string, string>): string[] {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const key of FRONTMATTER_ORDER) {
    const value = fm[key];
    if (value == null || String(value).trim() === '') continue;
    if (seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  for (const key of Object.keys(fm)) {
    const value = fm[key];
    if (value == null || String(value).trim() === '') continue;
    if (seen.has(key)) continue;
    seen.add(key);
    keys.push(key);
  }
  return keys;
}

/** Parse YAML tags: `[a, b]` or `a, b`. */
export function parseNoteFieldTags(raw: string): string[] {
  if (!raw || typeof raw !== 'string') return [];
  let s = raw.trim();
  if (
    (s.startsWith('[') && s.endsWith(']')) ||
    (s.startsWith('(') && s.endsWith(')'))
  ) {
    s = s.slice(1, -1);
  }
  return s
    .split(',')
    .map((t) => t.trim().replace(/^["']|["']$/g, '').trim())
    .filter(Boolean);
}

export function formatNoteFieldTags(tags: string[]): string {
  const unique = [...new Set(tags.map((t) => t.trim()).filter(Boolean))];
  return unique.length ? `[${unique.join(', ')}]` : '';
}

export function frontmatterFieldIcon(key: string): LucideIcon {
  switch (key) {
    case 'status':
      return CircleDot;
    case 'priority':
      return Flag;
    case 'category':
      return Layers;
    case 'tags':
      return Tags;
    case 'completed_date':
      return CalendarCheck;
    case 'mr_url':
    case 'git_mr_url':
      return GitMerge;
    case 'reviewed_by':
      return UserCheck;
    case 'blocker':
    case 'blockers':
      return OctagonAlert;
    case 'complexity':
      return Layers;
    case 'related_todos':
      return Link2;
    case 'estimated_time':
      return Clock;
    case 'working_branch':
    case 'branch':
      return GitBranch;
    default:
      return Rows3;
  }
}

const TAGS_VISIBLE = 2;

function NoteFieldTags({ raw }: { raw: string }) {
  const tags = parseNoteFieldTags(raw);
  if (tags.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  const visible = tags.slice(0, TAGS_VISIBLE);
  const overflow = tags.length - visible.length;
  return (
    <div className="group/tags relative min-w-0">
      <div className="flex min-w-0 items-center gap-1">
        {visible.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className="h-6 max-w-[8rem] truncate px-2 text-xs font-medium"
          >
            {tag}
          </Badge>
        ))}
        {overflow > 0 ? (
          <span className="shrink-0 text-xs text-muted-foreground">+{overflow}</span>
        ) : null}
      </div>
      {overflow > 0 ? (
        <div className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden max-w-[16rem] flex-wrap gap-1 rounded-md border border-border/60 bg-popover p-1.5 shadow-sm group-hover/tags:flex">
          {tags.map((tag) => (
            <Badge
              key={`all-${tag}`}
              variant="secondary"
              className="h-6 px-2 text-xs font-medium"
            >
              {tag}
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function frontmatterToPreBlock(fm: Record<string, string>): string {
  const lines = existingFrontmatterKeys(fm).map((key) => {
    const value = String(fm[key]);
    return value.includes('\n') ? `${key}: "${value.replace(/"/g, '\\"')}"` : `${key}: ${value}`;
  });
  if (lines.length === 0) return '';
  return `<pre>---\n${lines.join('\n')}\n---</pre>\n`;
}

function MetadataRow({
  icon: Icon,
  label,
  value,
  href,
  onClick,
  valueNode,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  href?: string;
  onClick?: () => void;
  valueNode?: ReactNode;
}) {
  const valueEl = valueNode ? (
    valueNode
  ) : href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-foreground font-medium hover:underline truncate max-w-[200px]"
      title={value}
    >
      {value}
    </a>
  ) : onClick ? (
    <button
      onClick={onClick}
      className="text-foreground font-medium hover:underline cursor-pointer truncate max-w-[200px]"
      title="Open comments"
      type="button"
    >
      {value}
    </button>
  ) : (
    <span className="text-foreground font-medium truncate" title={value}>
      {value}
    </span>
  );

  return (
    <div
      className={`flex items-center gap-3 text-sm ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick && !href ? onClick : undefined}
    >
      <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <span className="text-muted-foreground">{label}:</span>
      {valueEl}
    </div>
  );
}

export function TodoMetadataGrid({ todo, onOpenComments }: TodoMetadataGridProps) {
  const todoAny = todo as { description?: string; notes?: string };
  const rawDescription = todoAny.description || todoAny.notes || '';
  const frontmatter = parseFrontmatterFromDescription(rawDescription);

  const basecampItems: Array<{
    icon: ComponentType<{ className?: string }>;
    label: string;
    value: string;
    onClick?: () => void;
  }> = [];
  if (todo.comments_count !== undefined) {
    basecampItems.push({
      icon: MessageSquare,
      label: 'Comments',
      value: String(todo.comments_count),
      onClick: onOpenComments
    });
  }
  const frontmatterItems: Array<{
    icon: ComponentType<{ className?: string }>;
    label: string;
    value: string;
    href?: string;
    valueNode?: ReactNode;
  }> = [];
  for (const key of FRONTMATTER_ORDER) {
    const val = frontmatter[key];
    if (!val) continue;
    const label = FRONTMATTER_LABELS[key] || key.replace(/_/g, ' ');
    const isUrl = key === 'mr_url' || key === 'git_mr_url';
    const mrMatch = val.match(/merge_requests\/(\d+)/);
    let displayValue: string;
    if (key === 'completed_date' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      displayValue = parseLocalDate(val).toLocaleDateString();
    } else if (isUrl) {
      displayValue = mrMatch?.[1] ? `!${mrMatch[1]}` : val;
    } else if (key === 'status' && /^[a-z]/i.test(val)) {
      displayValue = val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
    } else {
      displayValue = val;
    }
    frontmatterItems.push({
      icon: frontmatterFieldIcon(key),
      label,
      value: displayValue,
      href: isUrl ? val : undefined,
      valueNode: key === 'tags' ? <NoteFieldTags raw={val} /> : undefined,
    });
  }

  const allItems: Array<{
    icon: ComponentType<{ className?: string }>;
    label: string;
    value: string;
    href?: string;
    onClick?: () => void;
    valueNode?: ReactNode;
  }> = [
    ...basecampItems.map((i) => ({ ...i, href: undefined })),
    ...frontmatterItems
  ];
  if (
    allItems.length === 0 &&
    !todo.due_on &&
    !todo.creator &&
    (!todo.assignees || todo.assignees.length === 0)
  ) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 sm:gap-y-4 py-4 border-y border-border">
      {todo.due_on ? (
        <div className="flex items-center gap-3 text-sm">
          <Calendar className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">Due:</span>
          <span className="truncate font-medium text-foreground">
            {parseLocalDate(todo.due_on).toLocaleDateString()}
          </span>
          {todo.completed ? null : (
            <BasecampDueBadge
              dueOn={todo.due_on}
              statusOnly
              className={BASECAMP_DUE_BADGE_COMPACT_CLASS}
            />
          )}
        </div>
      ) : null}
      {allItems.map((item, i) => (
        <MetadataRow
          key={i}
          icon={item.icon}
          label={item.label}
          value={item.value}
          href={item.href}
          onClick={item.onClick}
          valueNode={item.valueNode}
        />
      ))}
      {todo.creator ? (
        <div className="flex items-center gap-3 text-sm">
          <User className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">By:</span>
          <TodoPersonChip person={todo.creator} />
        </div>
      ) : null}
      {todo.assignees && todo.assignees.length > 0 && (
        <div className="flex items-center gap-3 text-sm sm:col-span-2">
          <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">Assignees:</span>
          <div className="flex flex-wrap gap-1.5">
            {todo.assignees.map((assignee) => (
              <TodoPersonChip key={assignee.id} person={assignee} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
